/**
 * Helpers de presentación del dominio de Horario.
 *
 * Puro formato/derivación para la UI; sin lógica de negocio remota ni acceso a la
 * API (`docs/frontend_architecture.md`, Pr. 6). Replica el formato del SSR
 * (`schedule.html`/`laser_schedule.html`): rango de fechas de la semana, color de
 * cada celda según las pendientes, etiqueta del conteo, y las etiquetas de estado /
 * estado de pago del detalle de un slot (mismas que pintaba el modal del SSR).
 */
import { ApiError } from "@/lib/api";

import { SCHEDULING_GENERIC_ERROR } from "./constants";
import type { ScheduleCell, SlotOrder } from "./types";

/**
 * Traduce un error de la API/red a un mensaje legible. El horario es de solo
 * lectura: no hay `error_code` de negocio que mapear, así que se usa el `message`
 * del `ApiError` (ya en español del backend) o del `Error`, con un texto de
 * respaldo. (Un actor multi-sede sin `X-Sede-Id` recibe el mensaje del backend
 * pidiendo la sede, que se muestra tal cual.)
 */
export function schedulingErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return SCHEDULING_GENERIC_ERROR;
}

/**
 * Etiqueta del rango de la semana (réplica del encabezado del SSR
 * `current_week_start.strftime('%d/%m') - week_end.strftime('%d/%m/%Y')`). El
 * backend entrega los límites en ISO 8601; se parten los componentes del string
 * (sin construir `Date`) para no introducir corrimientos de zona horaria respecto a
 * la hora Colombia que ya usa el backend. Tolera límites ausentes.
 */
export function weekRangeLabel(
  weekStart: string | null,
  weekEnd: string | null,
): string {
  if (!weekStart || !weekEnd) {
    return "";
  }
  const start = weekStart.slice(0, 10).split("-"); // [YYYY, MM, DD]
  const end = weekEnd.slice(0, 10).split("-");
  const startLabel = `${start[2]}/${start[1]}`;
  const endLabel = `${end[2]}/${end[1]}/${end[0]}`;
  return `${startLabel} - ${endLabel}`;
}

/** Variante de color de una celda del horario (mapea a un estilo en la pantalla). */
export type CellTone = "empty" | "low" | "medium" | "high" | "delivered";

/**
 * Color de una celda según sus pendientes (réplica EXACTA de la lógica del SSR):
 *  - sin órdenes (`total === 0`)            -> `empty` (texto gris "0");
 *  - con pendientes 1-2                     -> `low` (verde);
 *  - con pendientes 3-4                     -> `medium` (amarillo);
 *  - con pendientes 5+                      -> `high` (rojo);
 *  - sin pendientes pero con entregadas     -> `delivered` (gris/secundario).
 */
export function cellTone(cell: ScheduleCell): CellTone {
  if (cell.total <= 0) {
    return "empty";
  }
  if (cell.pending > 0) {
    if (cell.pending <= 2) {
      return "low";
    }
    if (cell.pending <= 4) {
      return "medium";
    }
    return "high";
  }
  return "delivered";
}

/**
 * Texto del botón de una celda (réplica del SSR): si hay pendientes, muestra el
 * número de pendientes y, si además hay entregadas, `+N`; si no hay pendientes pero
 * sí entregadas, muestra `(N)`. Las celdas vacías no llaman a esta función (pintan
 * un "0" gris).
 */
export function cellLabel(cell: ScheduleCell): string {
  if (cell.pending > 0) {
    return cell.delivered > 0
      ? `${cell.pending}+${cell.delivered}`
      : `${cell.pending}`;
  }
  return `(${cell.delivered})`;
}

/** Etiquetas de estado de la orden del modal de slot (réplica del SSR). */
const STATUS_LABELS: Record<string, string> = {
  pendiente: "Pendiente",
  completado: "Completado",
  entregado: "Entregado",
};

/** Etiqueta legible del estado de una orden; el desconocido se muestra tal cual. */
export function slotStatusLabel(status: string | null): string {
  if (!status) {
    return "";
  }
  return STATUS_LABELS[status] ?? status;
}

/** Etiquetas de estado de pago del modal de slot (réplica del SSR). */
const PAYMENT_LABELS: Record<string, string> = {
  pendiente_de_pago: "Pendiente de pago",
  pagada_para_despacho: "Pagada para despacho",
  abonado: "Abonado",
  pagado: "Pagado",
  orden_a_credito: "Orden a crédito",
};

/**
 * Etiqueta legible del estado de pago (réplica del SSR, que ante un valor ausente
 * mostraba "Pendiente de pago"). Un valor desconocido se muestra tal cual.
 */
export function paymentStatusLabel(paymentStatus: string | null): string {
  if (!paymentStatus) {
    return "Pendiente de pago";
  }
  return PAYMENT_LABELS[paymentStatus] ?? paymentStatus;
}

/**
 * Formato del monto de venta de una orden de slot. El backend puede entregar
 * `sale_amount` como número o como string del modelo; se normaliza a número y se
 * formatea con separador de miles (es-CO). Réplica del `parseFloat(...).toFixed(2)`
 * del SSR, pero con separador local. Devuelve `null` si no hay un valor numérico.
 */
export function formatSaleAmount(value: SlotOrder["sale_amount"]): string | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numeric = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return null;
  }
  return `$${numeric.toLocaleString("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
